import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value = 0, size = 14 }) => (
  <span className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={0}
        className={i < Math.round(value) ? "fill-[#C9A227] text-[#C9A227]" : "fill-[#E8DCC4] text-[#E8DCC4]"}
      />
    ))}
  </span>
);
export { Stars };

const inputCls =
  "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300";

// Product reviews (P1.12 basic): real customer reviews only, admin-moderated
// before anything appears publicly. Nothing is seeded or fabricated.
export const ProductReviews = ({ sku }) => {
  const [data, setData] = useState({ items: [], summary: { count: 0, average: null } });

  useEffect(() => {
    if (!sku) return;
    axios
      .get(`${API}/reviews/${encodeURIComponent(sku)}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [sku]);

  const { items, summary } = data;

  return (
    <section className="mt-16" data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold">Reviews</h2>
          {summary.count > 0 ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-neutral-700" data-testid="reviews-summary">
              <Stars value={summary.average} />
              <span className="font-semibold">{summary.average}</span>
              <span className="text-neutral-400">· {summary.count} review{summary.count === 1 ? "" : "s"}</span>
            </p>
          ) : (
            <p className="mt-2 text-sm text-neutral-500" data-testid="reviews-empty">
              No reviews yet for this piece.
            </p>
          )}
          <p className="mt-1.5 text-xs text-neutral-400" data-testid="reviews-verified-note">
            Only verified buyers review — the private link arrives with your delivery email.
          </p>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="reviews-list">
          {items.map((r) => (
            <article key={r.id} className="bg-white border border-[#E8DCC4] rounded-sm p-5" data-testid={`review-${r.id}`}>
              <Stars value={r.rating} />
              {r.title && <p className="mt-2 font-semibold text-neutral-900 text-sm">{r.title}</p>}
              <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{r.text}</p>
              {r.photo_url && (
                <img src={r.photo_url} alt={`${r.name}'s photo of this piece`} loading="lazy"
                  className="mt-3 w-28 h-28 object-cover border border-[#E8DCC4] rounded-sm" />
              )}
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">
                {r.name} <span className="text-[#C9A227]">·</span>{" "}
                {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                {r.verified_buyer && (
                  <span className="ml-2 text-[#A8871B] normal-case tracking-normal" data-testid={`review-verified-${r.id}`}>
                    · Verified buyer
                  </span>
                )}
              </p>
              {r.admin_reply && (
                <div className="mt-3 border-l-2 border-[#C9A227] bg-[#FDFBF7] px-4 py-3" data-testid={`review-reply-${r.id}`}>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-[#A8871B] font-semibold mb-1">Gold Time Jewelry replied</p>
                  <p className="text-sm text-neutral-600 leading-relaxed">{r.admin_reply}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

    </section>
  );
};
